import { z } from "zod";
import type { GfeSummary } from "./gfe";

/**
 * Client messages (chunk D). Each clinic can turn each one on or off per channel and
 * edit its wording. Placeholders in {braces} are filled in when it's sent.
 */

export const MESSAGE_KEYS = [
  "booking_confirmed",
  "mobile_request_received",
  "mobile_accepted",
  "mobile_declined",
  "forms_reminder",
  "forms_reminder_soon",
  "reminder_24h",
  "reminder_2h",
  "card_request",
  "card_link",
  "pay_link",
  "tip_link",
  "receipt",
  "fee_charged",
  "cancelled",
  "gfe_link",
  "gfe_forms",
  "plan_payment_failed",
] as const;
export type MessageKey = (typeof MESSAGE_KEYS)[number];

export interface MessageDefault {
  label: string;
  /** When it goes out. */
  when: string;
  /** Sent right away in answer to something the client just did: ignores quiet hours. */
  immediate: boolean;
  sms: boolean;
  email: boolean;
  smsText: string;
  emailSubject: string;
  emailBody: string;
}

export const MESSAGE_PLACEHOLDERS: Record<string, string> = {
  first_name: "Client's first name",
  clinic: "Clinic name",
  date: "Visit date (e.g. Friday, Oct 9)",
  time: "Visit time (e.g. 2:30 PM)",
  service: "Service booked",
  address: "Where (mobile visits)",
  forms_link: "Private forms link",
  link: "The link this message is about (pay, save card)",
  amount: "Amount",
  policy: "Cancellation policy",
  phone: "Clinic phone",
  reply_c: '"Reply C to confirm." while the visit waits for the client to confirm (replaced by {next_step} and {confirm_step})',
  visit_for: '"you\'re booked", or "Zachary is booked" in a parent\'s copy',
  booker_note: '"Jane booked this for you." when someone else booked it; for a minor booked by themselves, who must come with them. Empty in a parent\'s copy.',
  next_step: 'What to do next: "Tap to confirm and fill out your forms: (link)", "Tap to confirm: (link)", or the forms link alone once confirmed',
  confirm_step: '"Tap to confirm: (link)" while the visit waits for the client to confirm',
  receipt: "Itemized receipt",
  plan: "Membership name",
  guardian_note: "For minors: a parent or guardian must be there for the call",
  booking_note: 'When someone books for someone else: "Jane booked this for you." or "This visit is for Mia." Under 18 and booked for themselves: who must come with them.',
};

export const MESSAGE_DEFAULTS: Record<MessageKey, MessageDefault> = {
  booking_confirmed: {
    label: "Booking confirmed",
    when: "Right after a booking is made (clinic and events), or a mobile request is accepted",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Hi {first_name}, {visit_for} at {clinic} on {date} at {time}. {booker_note}{next_step}",
    emailSubject: "You're booked: {date} at {time}",
    emailBody:
      "Hi {first_name},\n\nGood news: {visit_for} for {service} on {date} at {time}. {booker_note}\n\n{next_step}\n\n{policy}\n\nQuestions? Call or text us at {phone}.\n\n{clinic}",
  },
  mobile_request_received: {
    label: "Mobile request received",
    when: "Right after a mobile visit is requested",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Hi {first_name}, we got your request for {date} at {time}. A nurse will confirm shortly; we'll text you as soon as they do.",
    emailSubject: "We got your request for {date}",
    emailBody:
      "Hi {first_name},\n\nThanks for your request for {service} on {date} at {time} at {address}. A nurse will confirm it shortly and we'll let you know.\n\nPlease complete your forms before your visit: {forms_link}\n\n{clinic}",
  },
  mobile_accepted: {
    label: "Mobile request accepted",
    when: "When a nurse accepts a mobile request",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Good news, {first_name}: your visit on {date} at {time} is confirmed. Forms: {forms_link}",
    emailSubject: "Confirmed: {date} at {time}",
    emailBody: "Hi {first_name},\n\nYour visit for {service} on {date} at {time} at {address} is confirmed.\n\nForms: {forms_link}\n\n{policy}\n\n{clinic}",
  },
  mobile_declined: {
    label: "Mobile request declined",
    when: "When a mobile request is declined or nobody could take it",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Sorry {first_name}, we can't make {date} at {time} work. Your card hasn't been charged. Call or text {phone} and we'll find another time.",
    emailSubject: "We couldn't confirm {date}",
    emailBody: "Hi {first_name},\n\nSorry, we can't make your requested visit on {date} at {time} work. Your card hasn't been charged.\n\nCall or text us at {phone} and we'll find another time.\n\n{clinic}",
  },
  forms_reminder: {
    label: "Forms reminder",
    when: "24 hours before the visit, if forms aren't signed",
    immediate: false,
    sms: true,
    email: true,
    smsText: "Hi {first_name}, please complete your forms before your visit tomorrow at {time}. {booking_note}{forms_link}",
    emailSubject: "Please complete your forms",
    emailBody: "Hi {first_name},\n\nPlease complete your forms before your visit on {date} at {time}: {forms_link}\n\n{clinic}",
  },
  forms_reminder_soon: {
    label: "Forms reminder, 3 hours before",
    when: "3 hours before the visit, if forms still aren't signed",
    immediate: false,
    sms: true,
    email: false,
    smsText: "Hi {first_name}, your visit at {clinic} is at {time} today. {booking_note}Your forms aren't done yet (about 5 minutes): {forms_link}",
    emailSubject: "Your forms for today",
    emailBody: "Hi {first_name},\n\nYour visit is at {time} today. {booking_note}Please complete your forms before you arrive: {forms_link}\n\n{clinic}",
  },
  reminder_24h: {
    label: "Reminder, 24 hours before",
    when: "24 hours before the visit",
    immediate: false,
    sms: true,
    email: true,
    smsText: "Reminder: {service} at {clinic} on {date} at {time}. {confirm_step}Need to change it? Call or text {phone}.",
    emailSubject: "Reminder: {date} at {time}",
    emailBody: "Hi {first_name},\n\nA reminder of your visit for {service} on {date} at {time}.\n\n{policy}\n\nNeed to change it? Call or text us at {phone}.\n\n{clinic}",
  },
  reminder_2h: {
    label: "Reminder, 2 hours before",
    when: "2 hours before the visit",
    immediate: false,
    sms: true,
    email: false,
    smsText: "See you soon, {first_name}! Your visit at {clinic} is at {time}.",
    emailSubject: "See you at {time}",
    emailBody: "Hi {first_name},\n\nSee you soon: your visit is at {time} today.\n\n{clinic}",
  },
  card_request: {
    label: "Save your card",
    when: "When staff send a booking's save-your-card link",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Hi {first_name}, please add a card for your visit on {date}: {link}",
    emailSubject: "Add a card for your visit on {date}",
    emailBody: "Hi {first_name},\n\nPlease add a card for your visit on {date} at {time}: {link}\n\n{policy}\n\n{clinic}",
  },
  card_link: {
    label: "Add a card on file",
    when: "When staff send a client a link to add a card to their account (or for a membership)",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Hi {first_name}, here's a secure link from {clinic} to add a card on file. Nothing is charged now: {link}",
    emailSubject: "Add a card on file with {clinic}",
    emailBody: "Hi {first_name},\n\nHere's a secure link to add a card on file with us. Nothing is charged now.\n\n{link}\n\nQuestions? Call or text us at {phone}.\n\n{clinic}",
  },
  pay_link: {
    label: "Pay link",
    when: "When staff send a bill to pay by link",
    immediate: true,
    sms: true,
    email: true,
    smsText: "{clinic}: you can pay {amount} here: {link}",
    emailSubject: "Your bill from {clinic}",
    emailBody: "Hi,\n\nYou can pay {amount} for the visit on {date} here: {link}\n\nThank you,\n{clinic}",
  },
  tip_link: {
    label: "Tip link",
    when: "When staff send a link to tip on a visit a voucher or package covered",
    immediate: true,
    sms: true,
    email: true,
    smsText: "{clinic}: thanks for coming in! Your visit is covered. If you'd like to leave a tip for your nurse: {link}",
    emailSubject: "Thanks for visiting {clinic}",
    emailBody: "Hi,\n\nThanks for coming in on {date}. Your visit is covered, so nothing is owed. If you'd like to leave a tip for your nurse, you can here: {link}\n\nThank you,\n{clinic}",
  },
  receipt: {
    label: "Receipt",
    when: "After a payment",
    immediate: true,
    sms: false,
    email: true,
    smsText: "Thanks, {first_name}! {clinic} received {amount}.",
    emailSubject: "Your receipt from {clinic}",
    emailBody: "Hi {first_name},\n\nThank you for visiting us on {date}.\n\n{receipt}\n\n{clinic}",
  },
  fee_charged: {
    label: "No-show / late-cancel fee",
    when: "When the fee is charged to the card on file",
    immediate: true,
    sms: true,
    email: true,
    smsText: "{clinic}: per our policy, a {amount} fee was charged to your card for the visit on {date}. Questions? Call {phone}.",
    emailSubject: "Fee for your visit on {date}",
    emailBody: "Hi {first_name},\n\nPer our policy, a {amount} fee was charged to your card on file for the visit on {date} at {time}.\n\n{policy}\n\nQuestions? Call or text us at {phone}.\n\n{clinic}",
  },
  plan_payment_failed: {
    label: "Membership payment failed",
    when: "When a monthly membership payment doesn't go through (the membership is on hold until it's paid)",
    immediate: true,
    sms: true,
    email: true,
    smsText: "{clinic}: your {plan} payment of {amount} didn't go through, so your membership is on hold. Pay or update your card here: {link}",
    emailSubject: "Your {plan} payment didn't go through",
    emailBody:
      "Hi {first_name},\n\nYour monthly {plan} payment of {amount} didn't go through, so your membership is on hold: your sessions and member pricing can't be used until it's paid.\n\nPay or update your card here: {link}\n\nQuestions? Call or text us at {phone}.\n\n{clinic}",
  },
  cancelled: {
    label: "Cancelled",
    when: "When a visit is cancelled",
    immediate: true,
    sms: true,
    email: true,
    smsText: "{clinic}: your visit on {date} at {time} is cancelled. Call or text {phone} to rebook.",
    emailSubject: "Cancelled: {date} at {time}",
    emailBody: "Hi {first_name},\n\nYour visit on {date} at {time} is cancelled.\n\nCall or text us at {phone} to rebook.\n\n{clinic}",
  },
  gfe_link: {
    label: "Good faith exam link",
    when: "When a good faith exam (Qualiphy) is sent",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Hi {first_name}, before your visit at {clinic}, please complete a quick health screening with a licensed provider: {link} {guardian_note}",
    emailSubject: "Your health screening for {clinic}",
    emailBody:
      "Hi {first_name},\n\nBefore your IV or injection, a licensed provider needs to complete a quick good faith exam with you. It takes a few minutes: a few questions, then a short video call.\n\nStart here: {link}\n\n{guardian_note}\n\nQuestions? Call or text us at {phone}.\n\n{clinic}",
  },
  gfe_forms: {
    label: "Intake and health screening (staff sent)",
    when: "When staff send a good faith exam for a visit: the intake first, which leads into the screening",
    immediate: true,
    sms: true,
    email: true,
    smsText: "Hi {first_name}, before your visit at {clinic}, please fill out your intake and quick health screening (about 10 minutes): {link} {guardian_note}",
    emailSubject: "Your intake and health screening for {clinic}",
    emailBody:
      "Hi {first_name},\n\nBefore your IV or injection, please fill out your intake. It leads right into a quick good faith exam with a licensed provider: a short video call.\n\nStart here: {link}\n\n{guardian_note}\n\nQuestions? Call or text us at {phone}.\n\n{clinic}",
  },
};

/** Fill {placeholders}; unknown ones are left out. */
export function fillTemplate(text: string, vars: Record<string, string>): string {
  return text.replace(/\{([a-z_]+)\}/g, (_, k: string) => vars[k] ?? "").replace(/[ \t]+\n/g, "\n").trim();
}

export const messageTemplateUpdateSchema = z.object({
  smsEnabled: z.boolean(),
  emailEnabled: z.boolean(),
  smsText: z.string().trim().min(1).max(600),
  emailSubject: z.string().trim().min(1).max(200),
  emailBody: z.string().trim().min(1).max(5000),
});
export type MessageTemplateUpdate = z.infer<typeof messageTemplateUpdateSchema>;

export interface MessageTemplateView extends MessageTemplateUpdate {
  key: MessageKey;
  label: string;
  when: string;
  immediate: boolean;
}

export interface MessageView {
  id: string;
  channel: "sms" | "email";
  direction: "out" | "in";
  templateKey: string | null;
  label: string | null;
  to: string;
  subject: string | null;
  body: string;
  status: "scheduled" | "sent" | "failed" | "skipped" | "received" | "canceled";
  at: string;
  sendAt: string | null;
  error: string | null;
  sentBy: string | null;
  /** Incoming email: files it came with (open with /api/messages/:id/attachments/:index). */
  attachments: { name: string; size: number; type: string }[];
  /**
   * Incoming email with a designed (HTML) version: shown from /api/messages/:id/html
   * (?images=1 also loads pictures from elsewhere; ?quoted=1 shows the earlier messages under it).
   */
  html: { remoteImages: boolean; quoted: boolean } | null;
}

/** A voicemail or missed call from the clinic's phone system (RingCentral). */
export interface PhoneEventView {
  id: string;
  kind: "voicemail" | "missed_call" | "answered" | "outgoing";
  direction: "in" | "out";
  /** The other party's number (the caller, or who the clinic called). */
  from: string;
  /** A number that isn't a client's: its name, or blocked as spam. */
  known: { name: string; blocked: boolean } | null;
  /** The caller ID name, when the phone system has one. */
  callerName: string | null;
  client: { id: string; name: string } | null;
  /** Other clients with this number (a household): pick one to link. */
  possibleClients: { id: string; name: string }[];
  /** Linked to the client from a number that isn't their mobile, so the link can be undone. */
  unlinkable: boolean;
  at: string;
  durationSeconds: number | null;
  hasAudio: boolean;
  transcript: string | null;
  transcriptStatus: "pending" | "done" | "none";
  handledAt: string | null;
  handledBy: string | null;
}

export const phoneEventLinkSchema = z.object({ clientId: z.string().uuid(), savePhone: z.boolean().default(true) });

/** One of a client's other numbers. */
export interface ClientPhoneView {
  id: string;
  phone: string;
  label: string;
  fromLink: boolean;
}
/** A number a text to the client can go to. */
export interface TextNumber {
  phone: string;
  label: string;
  mobile: boolean;
}
export const clientPhoneInputSchema = z.object({ phone: z.string().trim().min(7).max(30), label: z.string().trim().max(60).default("") });

/** A call ringing (or in progress) on the clinic's phones, with what staff want to see about the caller. */
export interface IncomingCallView {
  id: string;
  from: string;
  /** ended: over (shown a while after, and an unknown caller someone answered until they're sorted out). */
  status: "ringing" | "answered" | "ended";
  /** Not a client, but named (a vendor, a doctor's office). */
  knownName: string | null;
  /** The caller ID name the phone network sent (raw, e.g. "SMITH JOHN"), if any. */
  callerName: string | null;
  /** Someone picked up; who (RingCentral's name for that line), when known. */
  answered: boolean;
  answeredBy: string | null;
  /** When it ended (it stays up a while after, or until closed). */
  endedAt: string | null;
  startedAt: string;
  callers: {
    id: string;
    name: string;
    minor: boolean;
    tags: { name: string; color: string }[];
    nextVisit: { id: string; start: string; serviceName: string; status: string } | null;
    /** Good faith exam for their next visit (or the clinic default when no visit). */
    gfe: GfeSummary | null;
    /** Forms still to sign before the next visit. */
    formsNeeded: number;
    lastText: { body: string; at: string } | null;
  }[];
}

/** One client (or unknown number) on the Communication page, with their latest message. */
export interface ConversationView {
  /** The client, or null for a number no client has. */
  client: { id: string; name: string } | null;
  from: string | null;
  /** A number that isn't a client's: its name, or blocked as spam. */
  known: { name: string; blocked: boolean } | null;
  last: { body: string; at: string; direction: "out" | "in"; channel: "sms" | "email" };
  /** Their messages nobody has answered yet. */
  needsReply: number;
}

/** A message in the clinic-wide log (All messages). */
export interface LoggedMessageView extends MessageView {
  client: { id: string; name: string } | null;
}

export interface CommunicationCounts {
  /** People (clients or numbers) with texts nobody has answered. */
  texts: number;
  /** People (clients or addresses) with emails nobody has answered. */
  emails: number;
  calls: number;
}

/** `from`: a phone number, or an email address (unknown senders). */
export const conversationDoneSchema = z.object({ clientId: z.string().uuid().nullable().default(null), from: z.string().max(254).nullable().default(null) });
/** savePhone: save the number (or, for an email address, the email if they have none) on the client. */
export const conversationLinkSchema = z.object({ from: z.string().min(5).max(254), clientId: z.string().uuid(), savePhone: z.boolean().default(true) });
/** A number that isn't a client's: named, or blocked as spam. */
export interface KnownNumberView {
  id: string;
  phone: string;
  name: string;
  blocked: boolean;
  addedBy: string | null;
  createdAt: string;
}

/** Clinic settings → Phone: how the RingCentral contacts sync is going. */
/** Blocked numbers going straight to voicemail in RingCentral (Settings → Phone). */
export interface PhoneSpamStatus {
  /** Only production changes RingCentral (staging shares the clinic's real account). */
  allowed: boolean;
  blocked: number;
  /** RingCentral's rule has exactly the blocked numbers. */
  upToDate: boolean;
  syncedAt: string | null;
  lastError: string | null;
}

export interface PhoneContactsStatus {
  enabled: boolean;
  /** Only production keeps contacts (staging shares the clinic's real RingCentral account). */
  allowed: boolean;
  /** Numbers that should be in the contacts (clients' numbers, and named non-clients). */
  numbers: number;
  /** Of those, how many are there with the current name. */
  upToDate: number;
  /** Contacts Vaulted has in the phone system now (turned off: still to be removed). */
  inPhoneSystem: number;
  lastRunAt: string | null;
  lastError: string | null;
}

export const knownNumberInputSchema = z.object({ phone: z.string().trim().min(7).max(30), name: z.string().trim().max(80).default(""), blocked: z.boolean().default(false) });
export const knownNumberUpdateSchema = z.object({ name: z.string().trim().max(80), blocked: z.boolean() });

export const conversationUnlinkSchema = z.object({ from: z.string().min(5).max(30), clientId: z.string().uuid() });

export const staffMessageSchema = z.object({
  channel: z.enum(["sms", "email"]),
  /** A text: which of the client's numbers (their mobile, or a linked one like a parent's); none = their mobile. */
  to: z.string().trim().max(30).nullish(),
  subject: z.string().trim().max(200).default(""),
  body: z.string().trim().min(1).max(1600),
});

/** Quiet hours (clinic time, "HH:MM"): is this minute of the day inside them? */
export function inQuietHours(minute: number, start: string, end: string): boolean {
  const m = (s: string) => Number(s.slice(0, 2)) * 60 + Number(s.slice(3, 5));
  const a = m(start);
  const b = m(end);
  if (a === b) return false;
  return a < b ? minute >= a && minute < b : minute >= a || minute < b;
}

/** A Yealink desk phone that shows who's calling (Settings → Phone). */
export interface DeskPhoneView {
  id: string;
  name: string;
  /** "https://<public address>:<port>" */
  address: string;
  pushUser: string;
  hasPassword: boolean;
  lastCallAt: string | null;
  lastError: string | null;
}

export const deskPhoneSchema = z.object({
  name: z.string().trim().min(1, "Name the phone").max(80),
  address: z
    .string()
    .trim()
    .max(200)
    .refine((v) => /^https:\/\/[^/\s]+(:\d{1,5})?\/?$/i.test(v), "Use https://<address>:<port> (the phone's forwarded HTTPS port)"),
  pushUser: z.string().trim().max(80).default(""),
  /** Blank on a change: keep the saved one. */
  pushPassword: z.string().max(200).default(""),
});

/** The phone's incoming-call Action URL (shown once, when made). */
export interface DeskPhoneLink {
  phone: DeskPhoneView;
  actionUrl: string;
}
